/* eslint-disable */

import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import MemberAvatar from "../components/MemberAvatar";
import MemberBadge from "../components/MemberBadge";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { US_LOCATIONS, OTHER_LOCATION, isUsLocation } from "../lib/locations";
import { markHoursQrViewed } from "../lib/hoursShare";
import { invalidateSavedSpaces as invalidateSavedSpacesCache } from "../lib/savedSpaces";
import ZipPromptBanner from "../components/ZipPromptBanner";
import HoursLocationLink from "../components/HoursLocationLink";

/**
 * Resize an image file (from a phone photo picker) to a boutique 512x512 JPEG
 * data URL. Runs entirely in the browser, no upload endpoint needed. Keeps
 * user avatars fast, private, and cheap to store.
 */
async function fileToProfileImage(file, maxSize = 512, quality = 0.86) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode image"));
      img.onload = () => {
        // center-crop to square, then resize to maxSize
        const s = Math.min(img.width, img.height);
        const sx = (img.width - s) / 2;
        const sy = (img.height - s) / 2;
        const canvas = document.createElement("canvas");
        canvas.width = maxSize;
        canvas.height = maxSize;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, sx, sy, s, s, 0, 0, maxSize, maxSize);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function Profile() {
  const { user, updateProfile, deleteAccount } = useAuth();
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  // Session 58 · Two-field UX for the location editor. `locationChoice` is
  // either a US state name or the OTHER_LOCATION sentinel; when Other is
  // picked, `otherCountry` becomes the actual value flowing into `city`.
  const [locationChoice, setLocationChoice] = useState("");
  const [otherCountry, setOtherCountry] = useState("");
  const [picture, setPicture] = useState("");
  const [bio, setBio] = useState("");
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [website, setWebsite] = useState("");
  const [professionType, setProfessionType] = useState("");
  // Session 60.18 · ZIP auto-detect state
  const [postalCode, setPostalCode] = useState("");
  const [resolvedCity, setResolvedCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [zipStatus, setZipStatus] = useState({ state: "idle", label: "" });
  // Session 60.20 · Members Near You opt-in
  const [discoverableInDirectory, setDiscoverableInDirectory] = useState(true);
  const [professions, setProfessions] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [busy, setBusy] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [hoursQr, setHoursQr] = useState(null);
  const [qrExpanded, setQrExpanded] = useState(false);
  // Session 60.21 · Saved spaces the member wants to visit.
  const [savedSpaces, setSavedSpaces] = useState([]);
  // Session 60.25 · Métier Hours Check-Ins — "Places I've worked".
  const [checkIns, setCheckIns] = useState([]);
  const photoInputRef = useRef(null);
  const navigate = useNavigate();
  // Session 60.24 · App Store §5.1.1(v) — in-app account deletion state.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");

  const handlePhotoPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image is too large, please pick one under 12MB.");
      return;
    }
    setUploadingPhoto(true);
    try {
      const dataUrl = await fileToProfileImage(file);
      setPicture(dataUrl);
      // Immediately persist so members feel the update; also lets Google-avatar
      // users overwrite their default avatar without hitting Save.
      await updateProfile({ picture: dataUrl });
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error("Couldn't process that photo, try a different one.");
    } finally {
      setUploadingPhoto(false);
      // Reset input so the same file can be re-picked if needed
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };
  const removePhoto = async () => {
    if (!picture) return;
    setUploadingPhoto(true);
    try {
      setPicture("");
      await updateProfile({ picture: "" });
      toast.success("Profile photo removed");
    } catch { /* silent */ }
    setUploadingPhoto(false);
  };

  useEffect(() => {
    if (user) {
      setName(user.name);
      const savedCity = user.city || "";
      setCity(savedCity);
      // Session 58 · Rehydrate two-field location UX from the flat `city`
      // string: a US state name → dropdown; anything else → Other + country.
      if (!savedCity) {
        setLocationChoice("");
        setOtherCountry("");
      } else if (isUsLocation(savedCity)) {
        setLocationChoice(savedCity);
        setOtherCountry("");
      } else {
        setLocationChoice(OTHER_LOCATION);
        setOtherCountry(savedCity);
      }
      setPicture(user.picture || "");
      setBio(user.bio || "");
      setTitle(user.title || "");
      setCompany(user.company || "");
      setLinkedin(user.linkedin || "");
      setWebsite(user.website || "");
      setProfessionType(user.profession_type || "");
      // Session 60.18 · Hydrate ZIP-derived fields from /auth/me payload.
      setPostalCode(user.postal_code || "");
      setResolvedCity(user.resolved_city || "");
      setStateName(user.state || "");
      setStateCode(user.state_code || "");
      if (user.postal_code && user.resolved_city && user.state_code) {
        setZipStatus({ state: "ok", label: `${user.resolved_city}, ${user.state_code}` });
      } else {
        setZipStatus({ state: "idle", label: "" });
      }
      // Session 60.20 · Hydrate directory opt-in flag (default true).
      setDiscoverableInDirectory(user.discoverable_in_directory !== false);
    }
  }, [user]);

  useEffect(() => {
    api.get("/bookings/me").then(r => setBookings(r.data)).catch(() => {});
    api.get("/options/professions").then(r => setProfessions(r.data)).catch(() => {});
    api.get("/me/pending-reviews").then(r => setPendingReviews(r.data)).catch(() => {});
  }, []);

  // Founding members get a Métier Hours QR to present at participating partner
  // cafés. Non-founding members receive a 403 which we swallow silently.
  useEffect(() => {
    if (user && user.founding_member) {
      api.get("/me/hours-qr")
        .then(r => { setHoursQr(r.data); if (r.data) markHoursQrViewed(); })
        .catch(() => setHoursQr(null));
    }
  }, [user && user.founding_member]);

  // Session 60.21 · Load saved spaces once when the profile mounts. Uses
  // the denormalized snapshot so the list renders without cross-collection
  // joins on the client.
  const loadSavedSpaces = async () => {
    try {
      const r = await api.get("/me/saved-spaces");
      setSavedSpaces(r.data?.items || []);
    } catch {
      setSavedSpaces([]);
    }
  };
  useEffect(() => { if (user) loadSavedSpaces(); }, [user]);

  // Session 60.25 · Load member's Métier Hours check-in history for the
  // "Places I've worked" section. Silent-fallback to empty on any error.
  useEffect(() => {
    if (!user) return;
    api.get("/hours/check-ins/me?limit=20")
      .then(r => setCheckIns(r.data?.items || []))
      .catch(() => setCheckIns([]));
  }, [user]);

  const removeSaved = async (space_type, space_id) => {
    try {
      await api.delete(`/me/saved-spaces/${space_type}/${encodeURIComponent(space_id)}`);
      setSavedSpaces((s) => s.filter(x => !(x.space_type === space_type && x.space_id === space_id)));
      invalidateSavedSpacesCache();
    } catch { /* non-fatal */ }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateProfile({
        name, city, picture, bio, title, company, linkedin, website,
        profession_type: professionType || null,
        // Session 60.18 · Include the ZIP-derived fields on save.
        postal_code: postalCode || null,
        resolved_city: resolvedCity || null,
        state: stateName || null,
        state_code: stateCode || null,
        // Session 60.20 · Persist Members-Near-You directory opt-in.
        discoverable_in_directory: discoverableInDirectory,
      });
      toast.success("Profile updated");
    }
    catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  // Session 60.18 · ZIP auto-detect: fires on input change, snaps the
  // location dropdown when the resolved state matches US_LOCATIONS.
  const onZipChange = async (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 5);
    setPostalCode(raw);
    if (raw.length !== 5) {
      setZipStatus({ state: "idle", label: "" });
      return;
    }
    setZipStatus({ state: "looking", label: "Detecting..." });
    try {
      const r = await api.get(`/geo/zip-lookup?zip=${raw}`);
      const { city: rCity, state: rState, state_code: rCode } = r.data;
      setResolvedCity(rCity || "");
      setStateName(rState || "");
      setStateCode(rCode || "");
      if (rState && isUsLocation(rState)) {
        setLocationChoice(rState);
        setCity(rState);
      }
      setZipStatus({ state: "ok", label: `${rCity}, ${rCode || rState}` });
    } catch {
      setZipStatus({ state: "miss", label: "No match — pick a location below" });
    }
  };

  if (!user) return null;

  const joinedDate = user.created_at ? new Date(user.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : null;
  const regionLabel = (() => {
    const c = (user.city || "").trim();
    if (!c) return null;
    if (["New Hampshire", "Massachusetts"].includes(c)) return "Métier";
    if (["Washington DC", "Maryland", "Virginia"].includes(c)) return "DMV";
    return c;
  })();

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="m-page-pad">
        <span className="m-eyebrow">Your account</span>
        <h1 className="m-h1">My <em>profile</em>.</h1>

        <ZipPromptBanner variant="compact" />

        <div style={{ margin: "10px 0 18px" }} data-testid="profile-member-badge">
          <MemberBadge
            member_type={user.member_type}
            founding_member={user.founding_member}
            is_official_account={user.is_official_account}
            size="md"
          />
        </div>

        {user.founding_member && !user.is_official_account && (
          <section className="m-founding-panel" data-testid="founding-member-panel">
            {/* Session 60.7 · Founding Member profile cleanup — the primary
                gold badge sits above (via MemberBadge). This panel no
                longer repeats the "You're a Founding Member" heading or
                the redundant "◆ Founding Member" chip. We keep the QR
                block + join metadata because they add information, not
                signage. */}
            <span className="m-founding-eyebrow">Founding Member · {new Date(user.created_at || Date.now()).getFullYear()}</span>
            <div className="m-founding-chips">
              {joinedDate && (
                <span className="m-founding-chip" data-testid="founding-chip-joined">
                  <span className="m-founding-chip-key">Joined</span> {joinedDate}
                </span>
              )}
              {regionLabel && (
                <span className="m-founding-chip" data-testid="founding-chip-region">
                  <span className="m-founding-chip-key">Region</span> {regionLabel}
                </span>
              )}
              {user.profession_label && (
                <span className="m-founding-chip" data-testid="founding-chip-profession">
                  <span className="m-founding-chip-key">Identity</span> {user.profession_label}
                </span>
              )}
            </div>

            {hoursQr && (
              <div className="m-hours-qr" data-testid="founding-hours-qr">
                <div className="m-hours-qr-copy">
                  <span className="m-hours-qr-eyebrow">◆ Métier Hours</span>
                  <h3 className="m-hours-qr-h">Your member QR code</h3>
                  <p className="m-hours-qr-sub">
                    Present this at participating Métier Hours partner cafés to redeem your founding-member benefit at the register.
                  </p>
                  <p className="m-hours-qr-fine">
                    Not all cafés in the directory are active partners yet. Look for the <em>Métier Hours Partner</em> badge on the <Link to="/hours" style={{color:"var(--gold)"}}>Métier Hours page</Link>.
                  </p>
                </div>
                <div className="m-hours-qr-code">
                  <button
                    type="button"
                    className="m-hours-qr-btn"
                    onClick={() => { markHoursQrViewed(); setQrExpanded(true); }}
                    aria-label="Enlarge Métier Hours QR code"
                    data-testid="founding-hours-qr-open"
                  >
                    <img
                      src={hoursQr.qr_svg_data_uri}
                      alt="Métier Hours QR code — present at participating partner cafés"
                      data-testid="founding-hours-qr-img"
                    />
                    <span className="m-hours-qr-hint">Tap to enlarge</span>
                  </button>
                  <span className="m-hours-qr-caption">{hoursQr.member_name}</span>
                </div>
              </div>
            )}

            {hoursQr && qrExpanded && (
              <div
                className="m-qr-lightbox"
                role="dialog"
                aria-modal="true"
                aria-label="Métier Hours QR code enlarged"
                onClick={() => setQrExpanded(false)}
                data-testid="founding-hours-qr-lightbox"
              >
                <div
                  className="m-qr-lightbox-inner"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="m-qr-lightbox-close"
                    onClick={() => setQrExpanded(false)}
                    aria-label="Close enlarged QR"
                    data-testid="founding-hours-qr-close"
                  >
                    ×
                  </button>
                  <img
                    src={hoursQr.qr_svg_data_uri}
                    alt="Métier Hours QR code enlarged"
                    className="m-qr-lightbox-img"
                    data-testid="founding-hours-qr-lightbox-img"
                  />
                  <div className="m-qr-lightbox-meta">
                    <span className="m-qr-lightbox-name">{hoursQr.member_name}</span>
                    <span className="m-qr-lightbox-status">Métier Founding Member</span>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        <div className="m-profile-grid">
          <section className="m-card m-profile-section">
            <div className="m-profile-header">
              <MemberAvatar user={{ ...user, picture }} size={96} clickable={false} />
              <div>
                <h2 className="m-h2">{user.name}</h2>
                <p className="m-muted">{user.email}</p>
                {title && <p className="m-muted small">{title}{company ? ` · ${company}` : ""}</p>}
                <div className="m-profile-badges">
                  {/* Session 60.7 · Removed duplicate "Founding Member"
                      text chip + admin "Team" gold block. The single
                      gold Founding Member badge above (from MemberBadge)
                      is now the sole identity signage on this profile.
                      Official accounts render zero badges via
                      is_official_account. */}
                </div>
              </div>
            </div>
            <form onSubmit={save} className="m-form mt-4">
              <label className="m-field"><span>Display name</span><input value={name} onChange={(e) => setName(e.target.value)} required data-testid="profile-name" /></label>
              <label className="m-field"><span>Profile photo</span>
                <div className="m-photo-picker" data-testid="profile-photo-picker">
                  <div className="m-photo-picker-preview" data-testid="profile-photo-preview">
                    {picture ? (
                      // Session 58.5 · same fallback contract as MemberAvatar —
                      // never render a broken-image icon if the data URL is
                      // corrupt or the pasted URL is unreachable. Swap in the
                      // initials pill instead.
                      <img
                        src={picture}
                        alt="Profile"
                        onError={(e) => {
                          const el = e.currentTarget;
                          el.style.display = "none";
                          const parent = el.parentElement;
                          if (parent && !parent.querySelector(".m-photo-picker-initials")) {
                            const span = document.createElement("span");
                            span.className = "m-photo-picker-initials";
                            span.textContent = user.initials || "M";
                            parent.appendChild(span);
                          }
                        }}
                      />
                    ) : (
                      <span className="m-photo-picker-initials">{user.initials}</span>
                    )}
                  </div>
                  <div className="m-photo-picker-actions">
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoPick}
                      style={{ display: "none" }}
                      data-testid="profile-photo-input"
                    />
                    <button
                      type="button"
                      className="m-btn-gold m-photo-picker-btn"
                      onClick={() => photoInputRef.current && photoInputRef.current.click()}
                      disabled={uploadingPhoto}
                      data-testid="profile-photo-upload"
                    >
                      {uploadingPhoto ? "Uploading…" : picture ? "Change photo" : "Upload from photo library"}
                    </button>
                    {picture && (
                      <button
                        type="button"
                        className="m-btn-ghost m-photo-picker-remove"
                        onClick={removePhoto}
                        disabled={uploadingPhoto}
                        data-testid="profile-photo-remove"
                      >Remove</button>
                    )}
                    <p className="m-photo-picker-hint">Pick from your camera roll on phone or drag from your desktop. Resized to a boutique 512×512 crop, private to your account.</p>
                    <details className="m-photo-picker-url" data-testid="profile-photo-url-toggle">
                      <summary>Or paste a URL</summary>
                      <input value={picture.startsWith("data:") ? "" : picture} onChange={(e) => setPicture(e.target.value)} placeholder="https://… paste any image URL" data-testid="profile-picture" />
                    </details>
                  </div>
                </div>
              </label>
              <div className="m-form-grid-2">
                <label className="m-field"><span>Title</span><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Independent counsel" data-testid="profile-title" /></label>
                <label className="m-field"><span>Company</span><input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Self-employed" data-testid="profile-company" /></label>
              </div>
              <label className="m-field"><span>Bio</span>
                <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="A line or two about your work." style={{padding:"12px 14px",border:"1px solid var(--m-border)",borderRadius:9,background:"var(--cream)",fontSize:14,resize:"vertical"}} data-testid="profile-bio" />
              </label>
              <div className="m-form-grid-2">
                <label className="m-field"><span>LinkedIn URL</span><input value={linkedin} onChange={(e) => setLinkedin(e.target.value)} placeholder="https://linkedin.com/in/…" data-testid="profile-linkedin" /></label>
                <label className="m-field"><span>Website</span><input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" data-testid="profile-website" /></label>
              </div>
              <div className="m-form-grid-2">
                <label className="m-field"><span>How do you work?</span>
                  <select value={professionType} onChange={(e) => setProfessionType(e.target.value)} data-testid="profile-profession">
                    <option value="">— Prefer not to say —</option>
                    {professions.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </label>
                <label className="m-field"><span>Location</span>
                  <select
                    value={locationChoice}
                    onChange={(e) => {
                      const val = e.target.value;
                      setLocationChoice(val);
                      if (val === OTHER_LOCATION) {
                        setCity(otherCountry);
                      } else {
                        setCity(val);
                      }
                    }}
                    data-testid="profile-city"
                  >
                    <option value="">— Select —</option>
                    {US_LOCATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    <option value={OTHER_LOCATION}>Other (outside US)…</option>
                  </select>
                </label>
              </div>
              {/* Session 60.18 · ZIP auto-detect row.
                  Populates location fields + shows detected City, ST as a
                  clickable link to filter members by that state. */}
              <div className="m-form-grid-2">
                <label className="m-field" data-testid="profile-zip-field" id="zip">
                  <span>ZIP code <span style={{ opacity: 0.6, fontWeight: 400 }}>(auto-detects your state)</span></span>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    pattern="[0-9]*"
                    value={postalCode}
                    onChange={onZipChange}
                    placeholder="e.g. 17101"
                    autoComplete="postal-code"
                    data-testid="profile-zip"
                  />
                  {zipStatus.label && (
                    <span
                      data-testid="profile-zip-status"
                      style={{
                        fontSize: 11.5,
                        marginTop: 4,
                        color: zipStatus.state === "ok" ? "var(--gold, #B8913A)" : zipStatus.state === "miss" ? "#B34141" : "rgba(255,255,255,0.55)",
                        fontWeight: 500,
                      }}
                    >
                      {zipStatus.state === "ok" ? "\u2713 " : ""}{zipStatus.label}
                    </span>
                  )}
                </label>
                <div className="m-field" data-testid="profile-resolved-location">
                  <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", fontWeight: 600, marginBottom: 4, display: "block" }}>
                    Your locale
                  </span>
                  {resolvedCity && stateCode ? (
                    <Link
                      to={`/community?state=${encodeURIComponent(stateCode)}`}
                      data-testid="profile-locale-link"
                      style={{
                        color: "var(--gold, #B8913A)",
                        fontWeight: 600,
                        textDecoration: "none",
                        borderBottom: "1px solid rgba(184,145,58,0.4)",
                        paddingBottom: 2,
                        fontSize: 14.5,
                      }}
                    >
                      {resolvedCity}, {stateCode} &rarr;
                    </Link>
                  ) : (
                    <span style={{ fontSize: 13.5, color: "rgba(255,255,255,0.55)" }}>
                      Enter your ZIP to see members in your area.
                    </span>
                  )}
                </div>
              </div>
              {/* Session 60.20 · Members Near You opt-in toggle. Full-width
                  row underneath the ZIP grid so the copy has room to explain
                  what discoverability means without cramping the location
                  fields. */}
              <label
                className="m-field"
                data-testid="profile-discoverable-field"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: "14px 16px",
                  borderRadius: 12,
                  background: "rgba(184,145,58,0.10)",
                  border: "1px solid rgba(184,145,58,0.32)",
                  cursor: "pointer",
                  color: "#1B2340",
                }}
              >
                <input
                  type="checkbox"
                  checked={discoverableInDirectory}
                  onChange={(e) => setDiscoverableInDirectory(e.target.checked)}
                  data-testid="profile-discoverable-toggle"
                  style={{ marginTop: 3 }}
                />
                <span>
                  <span style={{ fontSize: 13.5, fontWeight: 600, display: "block", marginBottom: 4, color: "#1B2340" }}>
                    Show me in <em style={{ color: "#B8913A" }}>Members Near You</em>
                  </span>
                  <span style={{ fontSize: 12.5, color: "rgba(27,35,64,0.7)", lineHeight: 1.55 }}>
                    Other members within 60 miles can see your profile in the Community sidebar.
                    Uncheck to stay off the map — you&#39;ll still be able to see everyone else.
                  </span>
                </span>
              </label>
              {locationChoice === OTHER_LOCATION && (
                <div className="m-form-grid-2">
                  <label className="m-field" data-testid="profile-country-field">
                    <span>Country</span>
                    <input
                      type="text"
                      value={otherCountry}
                      onChange={(e) => {
                        setOtherCountry(e.target.value);
                        setCity(e.target.value);
                      }}
                      placeholder="e.g. United Kingdom"
                      data-testid="profile-country"
                    />
                  </label>
                </div>
              )}
              <button type="submit" className="m-btn-gold" disabled={busy} data-testid="profile-save">{busy ? "Saving…" : "Save changes"}</button>
            </form>
          </section>

          {/* Session 60.21 · Saved Spaces — cafés + workspaces the member
              has bookmarked to visit. Denormalized snapshot renders
              instantly; each row links out (external for cafés, internal
              for workspaces) and has a small Remove control. */}
          <section className="m-card" data-testid="saved-spaces-card">
            <span className="m-eyebrow" style={{ color: "var(--gold)" }}>Saved Spaces</span>
            <h3 className="m-side-title">Places you want to <em>visit</em></h3>
            {savedSpaces.length === 0 ? (
              <div className="m-muted small" style={{ marginTop: 6 }} data-testid="saved-spaces-empty">
                Tap <strong>Save</strong> on any café or workspace to add it here.
              </div>
            ) : (
              <div className="m-bookings-list" data-testid="saved-spaces-list">
                {savedSpaces.map((sp) => {
                  const snap = sp.snapshot || {};
                  const isCafe = sp.space_type === "cafe";
                  const cityLine = [snap.neighborhood, snap.city, snap.state, snap.region].filter(Boolean).join(" · ");
                  const href = isCafe
                    ? snap.external_url
                    : (snap.slug ? `/spaces/${snap.slug}` : null);
                  const RowLinkTag = isCafe ? "a" : Link;
                  const linkProps = isCafe
                    ? { href: href || "#", target: "_blank", rel: "noreferrer" }
                    : { to: href || "#" };
                  return (
                    <div
                      key={`${sp.space_type}:${sp.space_id}`}
                      className="m-booking-row"
                      data-testid={`saved-space-${sp.space_type}-${sp.space_id}`}
                      style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                      {href ? (
                        <RowLinkTag {...linkProps} style={{ color: "inherit", textDecoration: "none", flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ flex: 1 }}>
                            <div className="m-booking-ws">{snap.name || "—"}</div>
                            <div className="m-booking-meta">
                              <span style={{ color: "var(--gold, #B8913A)", fontWeight: 600, marginRight: 8 }}>
                                {isCafe ? "Café" : "Workspace"}
                              </span>
                              {cityLine || (isCafe ? "Métier Hours" : "Workspace")}
                              {snap.perk_label && (
                                <span style={{ marginLeft: 8, color: "var(--gold, #B8913A)", fontWeight: 600 }}>
                                  · {snap.perk_label}
                                </span>
                              )}
                            </div>
                          </div>
                        </RowLinkTag>
                      ) : (
                        <div style={{ flex: 1 }}>
                          <div className="m-booking-ws">{snap.name || "—"}</div>
                          <div className="m-booking-meta">{cityLine || (isCafe ? "Café" : "Workspace")}</div>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeSaved(sp.space_type, sp.space_id)}
                        data-testid={`saved-space-remove-${sp.space_type}-${sp.space_id}`}
                        title="Remove from Saved Spaces"
                        style={{
                          border: "1px solid rgba(255,255,255,0.15)",
                          background: "transparent",
                          color: "rgba(255,255,255,0.7)",
                          fontSize: 11,
                          padding: "5px 10px",
                          borderRadius: 999,
                          cursor: "pointer",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Session 60.25 · Métier Hours Check-Ins — a lightweight
              historical "Places I've worked" list. Hidden entirely when
              a member has no check-ins yet so the profile stays tidy. */}
          {checkIns.length > 0 && (
            <section className="m-card" data-testid="profile-checkins-card">
              <span className="m-eyebrow" style={{ color: "var(--gold)" }}>Métier Hours</span>
              <h3 className="m-side-title">Places I&apos;ve <em>worked</em></h3>
              <div className="m-bookings-list" data-testid="profile-checkins-list">
                {checkIns.map((ci) => {
                  const cafe = ci.cafe || {};
                  const cityLine = [cafe.city, cafe.state].filter(Boolean).join(", ");
                  const when = ci.checked_in_at
                    ? new Date(ci.checked_in_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                    : "";
                  return (
                    <div
                      key={ci.check_in_id}
                      className="m-booking-row"
                      data-testid={`profile-checkin-${ci.check_in_id}`}
                      style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
                    >
                      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                        <div style={{ marginBottom: 6 }}>
                          <HoursLocationLink
                            cafeId={cafe.cafe_id}
                            name={cafe.name || "Métier Hours location"}
                            testid={`profile-checkin-cafe-${ci.check_in_id}`}
                          />
                        </div>
                        <div className="m-booking-meta">
                          {cityLine || "Métier Hours"}
                          <span style={{ opacity: 0.55 }}> · {when}</span>
                          {ci.work_intent_label && (
                            <span style={{ marginLeft: 8, color: "var(--gold, #B8913A)", fontWeight: 600 }}>
                              · 🎯 {ci.work_intent_label}
                            </span>
                          )}
                        </div>
                      </div>
                      {ci.commons_shared && (
                        <span
                          data-testid={`profile-checkin-shared-${ci.check_in_id}`}
                          title="Shared to Commons"
                          style={{
                            fontSize: 10,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            padding: "4px 8px",
                            borderRadius: 999,
                            border: "1px solid rgba(184,145,58,0.4)",
                            color: "var(--gold, #B8913A)",
                            fontWeight: 600,
                          }}
                        >
                          Shared
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {pendingReviews.length > 0 && (
            <section className="m-card" data-testid="pending-reviews-card">
              <span className="m-eyebrow" style={{color:"var(--gold)"}}>Pending feedback</span>
              <h3 className="m-side-title">Rate your <em>recent stays</em></h3>
              <div className="m-bookings-list">
                {pendingReviews.map(p => (
                  <Link key={p.booking_id} to={`/reviews/${p.booking_id}`} className="m-booking-row m-pending-row" data-testid={`pending-review-${p.booking_id}`} style={{textDecoration:"none", color:"inherit", display:"flex"}}>
                    <div>
                      <div className="m-booking-ws">{p.workspace_name}</div>
                      <div className="m-booking-meta">{p.workspace_city}</div>
                    </div>
                    <div className="m-booking-when">
                      <div className="m-booking-date">{p.date}</div>
                      <div className="m-booking-slot" style={{color:"var(--gold)"}}>Rate this stay →</div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className="m-card">
            <h3 className="m-side-title">Your <em>bookings</em></h3>
            {bookings.length === 0 ? (
              <div className="m-empty small">No bookings yet. Reserve from the Workspaces page.</div>
            ) : (
              <div className="m-bookings-list" data-testid="my-bookings">
                {bookings.map((b) => (
                  <div key={b.booking_id} className="m-booking-row">
                    <div>
                      <div className="m-booking-ws">{b.workspace_name}</div>
                      <div className="m-booking-meta">{b.workspace_neighborhood} · {b.workspace_city}</div>
                    </div>
                    <div className="m-booking-when">
                      <div className="m-booking-date">{b.date}</div>
                      <div className="m-booking-slot">{b.slot} · {b.duration}h</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Session 60.24 · App Store §5.1.1(v) — In-app account deletion.
              Two-step confirmation (checkbox + typed "DELETE MY ACCOUNT").
              Never rendered for admins (backend also rejects admin self-delete). */}
          {user?.role !== "admin" && (
            <section className="m-card" data-testid="delete-account-section" style={{ borderColor: "rgba(179, 65, 65, 0.35)" }}>
              <h3 className="m-side-title" style={{ color: "#B34141" }}>Delete <em>account</em></h3>
              <p style={{ fontSize: 13, lineHeight: 1.55, color: "var(--m-muted, rgba(27,35,64,0.7))", margin: "0 0 12px" }}>
                Deleting your account will remove your profile, personal preferences, and community posts from Métier.
                Your booking history and any payments are preserved for financial and legal recordkeeping, but your identity
                is removed from those records. This action cannot be undone.
              </p>
              {!deleteOpen ? (
                <button
                  type="button"
                  className="m-btn-ghost"
                  data-testid="delete-account-open"
                  onClick={() => { setDeleteOpen(true); setDeleteErr(""); }}
                  style={{ borderColor: "#B34141", color: "#B34141" }}
                >
                  Delete my account…
                </button>
              ) : (
                <div data-testid="delete-account-confirm" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <label style={{ fontSize: 12.5, color: "var(--m-text)" }}>
                    <span style={{ display: "block", marginBottom: 4, letterSpacing: "0.04em" }}>Why are you leaving? <span style={{ opacity: 0.6, fontWeight: 400 }}>(optional)</span></span>
                    <input
                      type="text"
                      value={deleteReason}
                      maxLength={200}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      data-testid="delete-account-reason"
                      placeholder="Anything you'd like us to know"
                      style={{
                        width: "100%", padding: "10px 12px", fontSize: 13,
                        background: "#fff", color: "var(--m-text)",
                        border: "1px solid rgba(27,35,64,0.18)", borderRadius: 8,
                      }}
                    />
                  </label>
                  <label style={{ fontSize: 12.5, color: "var(--m-text)", display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <input type="checkbox" data-testid="delete-account-ack" id="delete-account-ack" />
                    <span style={{ lineHeight: 1.5 }}>
                      I understand this will permanently anonymize my Métier profile and cannot be undone. My booking history
                      will be retained for accounting but no longer associated with me.
                    </span>
                  </label>
                  <label style={{ fontSize: 12.5, color: "var(--m-text)" }}>
                    <span style={{ display: "block", marginBottom: 4 }}>To confirm, type <strong>DELETE MY ACCOUNT</strong></span>
                    <input
                      type="text"
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      data-testid="delete-account-confirm-input"
                      autoComplete="off"
                      style={{
                        width: "100%", padding: "10px 12px", fontSize: 13,
                        background: "#fff", color: "var(--m-text)",
                        border: "1px solid rgba(27,35,64,0.18)", borderRadius: 8,
                        letterSpacing: "0.04em",
                      }}
                    />
                  </label>
                  {deleteErr && (
                    <div className="m-form-err" data-testid="delete-account-error" style={{ fontSize: 12.5 }}>{deleteErr}</div>
                  )}
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="m-btn-ghost"
                      data-testid="delete-account-cancel"
                      onClick={() => { setDeleteOpen(false); setDeleteConfirm(""); setDeleteReason(""); setDeleteErr(""); }}
                      disabled={deleteBusy}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      data-testid="delete-account-submit"
                      disabled={deleteBusy || deleteConfirm.trim() !== "DELETE MY ACCOUNT"}
                      onClick={async () => {
                        setDeleteErr("");
                        const ackEl = typeof document !== "undefined" ? document.getElementById("delete-account-ack") : null;
                        if (!ackEl || !ackEl.checked) {
                          setDeleteErr("Please tick the acknowledgment box to continue.");
                          return;
                        }
                        if (deleteConfirm.trim() !== "DELETE MY ACCOUNT") {
                          setDeleteErr("Please type DELETE MY ACCOUNT exactly to confirm.");
                          return;
                        }
                        setDeleteBusy(true);
                        try {
                          await deleteAccount();
                          toast.success("Your Métier account has been deleted.");
                          setTimeout(() => navigate("/login", { replace: true }), 600);
                        } catch (err) {
                          const detail = err?.response?.data?.detail || "Account deletion failed. Contact support if this persists.";
                          setDeleteErr(detail);
                          setDeleteBusy(false);
                        }
                      }}
                      style={{
                        padding: "10px 18px", fontSize: 13, borderRadius: 8,
                        background: "#B34141", color: "#fff", border: "none",
                        cursor: deleteBusy ? "wait" : "pointer",
                        opacity: (deleteBusy || deleteConfirm.trim() !== "DELETE MY ACCOUNT") ? 0.55 : 1,
                        letterSpacing: "0.03em", fontWeight: 500,
                      }}
                    >
                      {deleteBusy ? "Deleting…" : "Permanently delete my account"}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </AppShell>
  );
}
